(function () {
  'use strict';

  /*
    SFG Support Code Engine
    =======================

    Shared parser / lookup / vehicle-context layer for:

      - Engine / vehicle DTCs
      - EFILive $xxxx error codes
      - SFG VIN decoder integration

    Data sources:
      window.SFGEngineDTCData
      window.SFGEFILiveErrorData

    VIN decoder:
      window.SFGVehicleDecoder

    IMPORTANT:
      This file does NOT read or write the decoder's saved VIN.
      Support analysis uses only the VIN explicitly supplied to it.
  */


  /* ==========================================================
     GENERAL HELPERS
     ========================================================== */

  function unique(list) {
    return list.filter(function (value, index, self) {
      return self.indexOf(value) === index;
    });
  }


  function cloneObject(obj) {
    var out = {};

    Object.keys(obj || {}).forEach(function (key) {
      out[key] = obj[key];
    });

    return out;
  }


  function mergeObjects(base, extra) {
    var out = cloneObject(base);

    Object.keys(extra || {}).forEach(function (key) {
      out[key] = extra[key];
    });

    return out;
  }


  function stringValue(value) {
    return String(value == null ? '' : value).trim();
  }


  function numberValue(value) {
    var number = Number.parseFloat(value);

    return Number.isFinite(number)
      ? number
      : null;
  }


  /* ==========================================================
     ENGINE DTC NORMALIZATION / EXTRACTION
     ========================================================== */

  function normalizeEngineCode(value) {
    if (!value) return null;

    var code = String(value)
      .toUpperCase()
      .replace(/\s+/g, '')
      .replace(/[^A-Z0-9]/g, '');

    /*
      Supports normal OBD-style codes such as:

        P0101
        P00AF
        P1089
        U0100
        C0035
        B1234
    */
    if (!/^[PBCU][0-9A-F]{4}$/.test(code)) {
      return null;
    }

    return code;
  }


  function extractEngineCodes(text) {
    if (!text) return [];

    var source = String(text).toUpperCase();

    var matches =
      source.match(/\b[PBCU][0-9A-F]{4}\b/g) || [];

    return unique(
      matches
        .map(normalizeEngineCode)
        .filter(Boolean)
    );
  }


  /* ==========================================================
     EFILIVE NORMALIZATION / EXTRACTION
     ========================================================== */

  function normalizeEFILiveCode(value) {
    if (!value) return null;

    var code = String(value)
      .toUpperCase()
      .trim()
      .replace(/\s+/g, '')
      .replace(/^\$/, '')
      .replace(/[^0-9A-F]/g, '');

    if (!code || code.length > 4) {
      return null;
    }

    code = code.padStart(4, '0');

    if (!/^[0-9A-F]{4}$/.test(code)) {
      return null;
    }

    return '$' + code;
  }


  function extractEFILiveCodes(text) {
    if (!text) return [];

    var source = String(text).toUpperCase();
    var results = [];

    /*
      Standard EFILive format:

        $0030
        $0537
        $054A
    */
    var dollarMatches =
      source.match(/\$[0-9A-F]{1,4}\b/g) || [];

    dollarMatches.forEach(function (match) {
      var code = normalizeEFILiveCode(match);

      if (code) {
        results.push(code);
      }
    });

    /*
      Also accept a bare 4-character code:

        0030
        0537
        054A

      Requiring all four characters prevents normal numbers
      from being interpreted too aggressively as EFILive errors.
    */
    var bareMatches =
      source.match(/\b[0-9A-F]{4}\b/g) || [];

    bareMatches.forEach(function (match) {
      var code = normalizeEFILiveCode(match);

      if (code) {
        results.push(code);
      }
    });

    return unique(results);
  }


  /* ==========================================================
     GENERIC DATABASE LOOKUP
     ========================================================== */

  function matchesFamily(code, family) {
    if (!family) return false;

    if (family.pattern instanceof RegExp) {
      family.pattern.lastIndex = 0;
      return family.pattern.test(code);
    }

    if (typeof family.test === 'function') {
      try {
        return !!family.test(code);
      } catch (error) {
        console.warn(
          'SFG Support Codes family test failed:',
          error
        );

        return false;
      }
    }

    if (family.prefix) {
      return code.indexOf(family.prefix) === 0;
    }

    return false;
  }


  function lookup(code, database) {
    if (!database) {
      return {
        code: code,
        matched: false,
        matchType: 'none',
        title: 'Code database unavailable',
        customerText:
          'The automated code database is not currently available. The exact code will still be retained with the support information.',
        action: 'collect_and_continue',
        ticketRecommended: true
      };
    }

    var exact = database.exact || {};

    if (exact[code]) {
      return mergeObjects(
        {
          code: code,
          matched: true,
          matchType: 'exact'
        },
        exact[code]
      );
    }

    var families = database.families || [];

    for (var i = 0; i < families.length; i++) {
      if (matchesFamily(code, families[i])) {
        return mergeObjects(
          {
            code: code,
            matched: true,
            matchType: 'family'
          },
          families[i]
        );
      }
    }

    return {
      code: code,
      matched: false,
      matchType: 'none',

      title:
        'Code not specifically recognized',

      customerText:
        'SFG does not have a specific automated diagnostic rule for this code. The exact code will be included with your support information rather than guessed.',

      staffText:
        'No automated SFG code rule matched this code.',

      action:
        'collect_and_continue',

      ticketRecommended:
        true
    };
  }


  /* ==========================================================
     VEHICLE CONTEXT
     ========================================================== */

  function buildVehicleContext(decoded) {
    decoded = decoded || {};

    var result =
      decoded.result || {};

    var display =
      decoded.display || {};

    var target =
      decoded.target ||
      display.target ||
      null;

    var manufacturer =
      stringValue(
        decoded.manufacturer ||
        display.manufacturer
      ).toLowerCase();

    var year =
      Number.parseInt(
        result.ModelYear ||
        display.year ||
        0,
        10
      );

    var displacement =
      numberValue(
        result.DisplacementL
      );

    return {
      vin:
        stringValue(
          decoded.vin ||
          display.vin
        ),

      manufacturer:
        manufacturer,

      year:
        Number.isFinite(year)
          ? year
          : 0,

      modelYear:
        Number.isFinite(year)
          ? year
          : 0,

      make:
        stringValue(
          result.Make
        ),

      model:
        stringValue(
          result.Model
        ),

      title:
        stringValue(
          display.title
        ),

      engine:
        stringValue(
          display.engine ||
          result.EngineModel
        ),

      engineModel:
        stringValue(
          result.EngineModel
        ),

      displacementL:
        displacement == null
          ? 0
          : displacement,

      fuel:
        stringValue(
          result.FuelTypePrimary
        ),

      truckSize:
        stringValue(
          display.truckSize
        ),

      rear:
        stringValue(
          display.rear
        ),

      drive:
        stringValue(
          display.drive
        ),

      body:
        stringValue(
          display.body
        ),

      fourWdControl:
        stringValue(
          display.fourWdControl
        ),

      transmission:
        stringValue(
          display.transmission
        ),

      target:
        target,

      targetFamily:
        target && target.family
          ? stringValue(target.family)
          : '',

      targetKey:
        target && target.key
          ? stringValue(target.key)
          : '',

      targetLabel:
        target && target.label
          ? stringValue(target.label)
          : '',

      metaText:
        stringValue(
          display.metaText
        )
    };
  }


  function decoderAvailable() {
    return !!(
      window.SFGVehicleDecoder &&
      typeof window.SFGVehicleDecoder.decodeVinValue === 'function' &&
      typeof window.SFGVehicleDecoder.validateVin === 'function'
    );
  }


  function validateVinForSupport(value) {
    if (
      window.SFGVehicleDecoder &&
      typeof window.SFGVehicleDecoder.validateVin === 'function'
    ) {
      return window.SFGVehicleDecoder.validateVin(value);
    }

    /*
      Basic fallback only if the shared decoder has not loaded.

      We deliberately do NOT attempt to duplicate the complete VIN
      validation logic here.
    */
    var vin = String(value || '')
      .toUpperCase()
      .replace(/[^A-HJ-NPR-Z0-9]/g, '')
      .slice(0, 17);

    if (vin.length !== 17) {
      return {
        valid: false,
        vin: vin,
        message:
          'Enter the full 17-digit VIN.'
      };
    }

    return {
      valid: true,
      vin: vin,
      message: ''
    };
  }


  async function decodeVehicleContext(vin, options) {
    options = options || {};

    var validation =
      validateVinForSupport(vin);

    if (!validation.valid) {
      var invalidError =
        new Error(
          validation.message ||
          'The VIN is not valid.'
        );

      invalidError.sfgVinInvalid = true;
      invalidError.vin =
        validation.vin || '';

      throw invalidError;
    }

    if (!decoderAvailable()) {
      var unavailableError =
        new Error(
          'The SFG vehicle decoder is not available on this page.'
        );

      unavailableError.sfgDecoderUnavailable =
        true;

      unavailableError.vin =
        validation.vin;

      throw unavailableError;
    }

    var decodeOptions = {};

    if (
      Number.isFinite(
        Number(options.timeoutMs)
      )
    ) {
      decodeOptions.timeoutMs =
        Number(options.timeoutMs);
    }

    var decoded =
      await window.SFGVehicleDecoder.decodeVinValue(
        validation.vin,
        decodeOptions
      );

    return {
      vin:
        validation.vin,

      vehicle:
        buildVehicleContext(decoded),

      decoded:
        decoded
    };
  }


  /* ==========================================================
     ENGINE LOOKUP — VEHICLE AWARE
     ========================================================== */

  function lookupEngineCode(value, vehicleContext) {
    var code =
      normalizeEngineCode(value);

    if (!code) {
      return {
        code: value,
        valid: false,
        matched: false,
        matchType: 'invalid',
        source: 'engine',
        title:
          'Invalid vehicle fault code'
      };
    }

    var database =
      window.SFGEngineDTCData;

    var result;

    /*
      New vehicle-aware database API.
    */
    if (
      database &&
      typeof database.resolve === 'function'
    ) {
      result =
        database.resolve(
          code,
          vehicleContext || {}
        );
    } else {
      /*
        Backward-compatible fallback.
      */
      result =
        lookup(
          code,
          database
        );
    }

    result =
      mergeObjects(
        result || {},
        {
          code: code,
          valid: true,
          source: 'engine'
        }
      );

    return result;
  }


  function analyzeEngineText(text, vehicleContext) {
    var codes =
      extractEngineCodes(text);

    var database =
      window.SFGEngineDTCData;

    /*
      Prefer the new correlated, vehicle-aware API.
    */
    if (
      database &&
      typeof database.analyzeSet === 'function'
    ) {
      var analyzed =
        database.analyzeSet(
          codes,
          vehicleContext || {}
        ) || {};

      var results =
        (analyzed.results || []).map(
          function (result) {
            return mergeObjects(
              result || {},
              {
                valid: true,
                source: 'engine'
              }
            );
          }
        );

      return mergeObjects(
        analyzed,
        {
          codes:
            analyzed.codes || codes,

          results:
            results,

          insights:
            analyzed.insights || [],

          count:
            codes.length,

          source:
            'engine'
        }
      );
    }

    /*
      Backward-compatible non-correlated analysis.
    */
    return {
      codes:
        codes,

      results:
        codes.map(function (code) {
          return lookupEngineCode(
            code,
            vehicleContext
          );
        }),

      insights:
        [],

      count:
        codes.length,

      source:
        'engine'
    };
  }


  /*
    VIN-aware engine analysis.

    Network/API failure does NOT have to prevent support.

    By default:

      - Invalid VIN -> throws
      - Valid VIN but decode service unavailable -> generic DTC analysis
      - Successful VIN decode -> vehicle-specific DTC analysis

    Set:

      genericOnDecodeFailure: false

    if a caller specifically wants VIN decoding to be mandatory.
  */

  async function analyzeEngineTextForVin(
    text,
    vin,
    options
  ) {
    options = options || {};

    var validation =
      validateVinForSupport(vin);

    if (!validation.valid) {
      var invalidError =
        new Error(
          validation.message ||
          'The VIN is not valid.'
        );

      invalidError.sfgVinInvalid = true;
      invalidError.vin =
        validation.vin || '';

      throw invalidError;
    }

    try {
      var decoded =
        await decodeVehicleContext(
          validation.vin,
          options
        );

      var analysis =
        analyzeEngineText(
          text,
          decoded.vehicle
        );

      return mergeObjects(
        analysis,
        {
          vin:
            validation.vin,

          vehicle:
            decoded.vehicle,

          decoded:
            decoded.decoded,

          vehicleDecoded:
            true,

          vehicleDecodeError:
            ''
        }
      );
    } catch (error) {
      /*
        Bad VIN remains a hard error.
      */
      if (error && error.sfgVinInvalid) {
        throw error;
      }

      /*
        Caller may choose to require successful VIN resolution.
      */
      if (
        options.genericOnDecodeFailure === false
      ) {
        throw error;
      }

      /*
        A valid VIN with an unavailable lookup service should not
        lock the customer out of support.

        Run safe generic DTC analysis instead.
      */
      var genericVehicle = {
        vin:
          validation.vin
      };

      var genericAnalysis =
        analyzeEngineText(
          text,
          genericVehicle
        );

      return mergeObjects(
        genericAnalysis,
        {
          vin:
            validation.vin,

          vehicle:
            genericVehicle,

          decoded:
            null,

          vehicleDecoded:
            false,

          vehicleDecodeError:
            error && error.message
              ? error.message
              : 'Vehicle lookup unavailable.'
        }
      );
    }
  }


  /* ==========================================================
     EFILIVE LOOKUP
     ========================================================== */

  function lookupEFILiveCode(value) {
    var code =
      normalizeEFILiveCode(value);

    if (!code) {
      return {
        code: value,
        valid: false,
        matched: false,
        matchType: 'invalid',
        source: 'efilive',
        title:
          'Invalid EFILive error code'
      };
    }

    var result =
      lookup(
        code,
        window.SFGEFILiveErrorData
      );

    result =
      mergeObjects(
        result,
        {
          code: code,
          valid: true,
          source: 'efilive'
        }
      );

    return result;
  }


  function analyzeEFILiveText(text) {
    var codes =
      extractEFILiveCodes(text);

    return {
      codes:
        codes,

      results:
        codes.map(
          lookupEFILiveCode
        ),

      count:
        codes.length,

      source:
        'efilive'
    };
  }


  /* ==========================================================
     STAFF FORMATTING
     ========================================================== */

  function formatVehicleContext(vehicle) {
    if (!vehicle) return '';

    var lines = [];

    if (vehicle.vin) {
      lines.push(
        'VIN: ' + vehicle.vin
      );
    }

    if (vehicle.title) {
      lines.push(
        'Vehicle: ' + vehicle.title
      );
    } else {
      var vehicleTitle = [
        vehicle.year,
        vehicle.make,
        vehicle.model
      ]
        .filter(Boolean)
        .join(' ');

      if (vehicleTitle) {
        lines.push(
          'Vehicle: ' + vehicleTitle
        );
      }
    }

    if (vehicle.engine) {
      lines.push(
        'Engine: ' + vehicle.engine
      );
    }

    if (vehicle.targetLabel) {
      lines.push(
        'Platform: ' +
        vehicle.targetLabel
      );
    } else if (vehicle.targetKey) {
      lines.push(
        'Platform: ' +
        vehicle.targetKey
      );
    }

    if (vehicle.transmission) {
      lines.push(
        'Transmission: ' +
        vehicle.transmission
      );
    }

    return lines.join('\n');
  }


  function formatStaffResult(result) {
    if (!result) return '';

    var lines = [];

    lines.push(
      result.code ||
      'Unknown code'
    );

    if (result.title) {
      lines.push(
        'Classification: ' +
        result.title
      );
    }

    if (result.category) {
      lines.push(
        'Category: ' +
        result.category
      );
    }

    if (result.matchType) {
      lines.push(
        'Rule Match: ' +
        result.matchType
      );
    }

    if (result.vehicleRule) {
      lines.push(
        'Vehicle Rule: ' +
        result.vehicleRule
      );
    }

    if (result.staffText) {
      lines.push(
        'SFG Note: ' +
        result.staffText
      );
    }

    if (result.action) {
      lines.push(
        'Action: ' +
        result.action
      );
    }

    if (
      result.stockTest !== undefined
    ) {
      lines.push(
        'Stock Test: ' +
        (
          result.stockTest
            ? 'Recommended'
            : 'Not specifically recommended'
        )
      );
    }

    if (result.stockTestBlocked) {
      lines.push(
        'Stock Test Blocked: Yes'
      );
    }

    if (result.stockTestNote) {
      lines.push(
        'Stock Test Note: ' +
        result.stockTestNote
      );
    }

    if (
      result.logRecommended !== undefined
    ) {
      lines.push(
        'Log: ' +
        (
          result.logRecommended
            ? 'Recommended'
            : 'Not specifically required'
        )
      );
    }

    if (result.likelyVehicleSide) {
      lines.push(
        'Likely Vehicle-Side: Yes'
      );
    }

    if (result.hardwareRevisionPossible) {
      lines.push(
        'Hardware Revision Possible: Yes'
      );
    }

    if (result.programmingRecovery) {
      lines.push(
        'Programming Recovery: Yes'
      );
    }

    if (result.unresolvedKey) {
      lines.push(
        'Unresolved Check Key: ' +
        result.unresolvedKey
      );
    }

    return lines.join('\n');
  }


  function formatStaffAnalysis(analysis) {
    if (!analysis) return '';

    var sections = [];

    if (analysis.vehicle) {
      var vehicleText =
        formatVehicleContext(
          analysis.vehicle
        );

      if (vehicleText) {
        sections.push(
          vehicleText
        );
      }
    }

    if (
      analysis.vehicleDecoded === false &&
      analysis.vehicleDecodeError
    ) {
      sections.push(
        'VIN LOOKUP WARNING:\n' +
        analysis.vehicleDecodeError
      );
    }

    (analysis.results || []).forEach(
      function (result) {
        var text =
          formatStaffResult(result);

        if (text) {
          sections.push(text);
        }
      }
    );

    if (
      analysis.insights &&
      analysis.insights.length
    ) {
      var insightLines = [
        'CORRELATED DIAGNOSTIC NOTES'
      ];

      analysis.insights.forEach(
        function (insight) {
          if (insight.title) {
            insightLines.push(
              insight.title
            );
          }

          if (insight.text) {
            insightLines.push(
              insight.text
            );
          }
        }
      );

      sections.push(
        insightLines.join('\n')
      );
    }

    return sections.join(
      '\n\n'
    );
  }


  /* ==========================================================
     PUBLIC API
     ========================================================== */

  window.SFGSupportCodes = {
    version: '2.0.0',

    /*
      Parsing
    */
    normalizeEngineCode:
      normalizeEngineCode,

    normalizeEFILiveCode:
      normalizeEFILiveCode,

    extractEngineCodes:
      extractEngineCodes,

    extractEFILiveCodes:
      extractEFILiveCodes,

    /*
      Vehicle
    */
    decoderAvailable:
      decoderAvailable,

    validateVin:
      validateVinForSupport,

    buildVehicleContext:
      buildVehicleContext,

    decodeVehicleContext:
      decodeVehicleContext,

    /*
      Engine
    */
    lookupEngineCode:
      lookupEngineCode,

    analyzeEngineText:
      analyzeEngineText,

    analyzeEngineTextForVin:
      analyzeEngineTextForVin,

    /*
      EFILive
    */
    lookupEFILiveCode:
      lookupEFILiveCode,

    analyzeEFILiveText:
      analyzeEFILiveText,

    /*
      Staff formatting
    */
    formatVehicleContext:
      formatVehicleContext,

    formatStaffResult:
      formatStaffResult,

    formatStaffAnalysis:
      formatStaffAnalysis
  };

})();